// @flow
import React from 'react';
import { formatDateDisplayUTC } from '@fortress-technology-solutions/fortress-component-library/utils';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Box,
  Spinner,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

import useIntl from '../../../hooks/useIntl';
import messages from './messages';
import { useUnitHistoryRows, useUnitHistoryStays } from './hooks';
import type { UnitHistoryStay } from './hooks';

export type { UnitHistoryStay } from './hooks';
export { UNIT_HISTORY_QUERY_KEY } from './hooks';

// A `btn-link` sized to its text, like the anchors it sits next to: the app's `.btn` rules add a
// minimum size, padding and margin meant for regular buttons.
export const LINK_BUTTON_STYLE = {
  padding: 0,
  margin: 0,
  minWidth: 0,
  minHeight: 0,
  border: 0,
  lineHeight: 'inherit',
  verticalAlign: 'baseline',
};

const NO_DATE = '---';

// Calendar days: formatted in UTC so the day never shifts with the browser's timezone.
const toDisplay = (isoDate: ?string): string =>
  isoDate ? formatDateDisplayUTC(isoDate, 'MM/DD/YYYY') : NO_DATE;

type Props = {
  residentId: string,
  organizationId: string,
  propertyId: string,
  onClose: () => void,
  /** Opens the Edit Move-In Date modal for the stay. */
  onEditStay: (stay: UnitHistoryStay) => void,
};

/**
 * Every unit the household lived in at this property, newest first, with a pencil per row that
 * corrects that stay's Move-In Date (household-mi-mo-date-changes). Mounted only while
 * open, so each opening shows the stays the server returns now.
 */
const UnitHistoryModal = ({
  residentId,
  organizationId,
  propertyId,
  onClose,
  onEditStay,
}: Props) => {
  const intl = useIntl();
  const { stays, isLoading, isError, refetch } = useUnitHistoryStays({
    organizationId,
    propertyId,
    residentId,
  });
  const { getStartedLabel, getEndedLabel, getEditLabel, handleEditStay } =
    useUnitHistoryRows(onEditStay);

  // The Snapshot's pencil as a button. The Tooltip's wrapper is the hover target: a disabled
  // button gets no pointer events, so the blocked reason still shows.
  const pencil = (stay: UnitHistoryStay) => {
    const isBlocked = Boolean(stay.blocked);
    return (
      <Tooltip
        title={stay.blocked?.message ?? ''}
        containerSX={{ display: 'inline' }}
      >
        <button
          type="button"
          className="btn btn-link"
          style={{
            ...LINK_BUTTON_STYLE,
            ...(isBlocked ? { pointerEvents: 'none' } : {}),
          }}
          onClick={handleEditStay(stay)}
          disabled={isBlocked}
          aria-label={getEditLabel(stay)}
          data-test="unit-history-edit-move-in"
        >
          <i className="icon et-pencil" />
        </button>
      </Tooltip>
    );
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" py={2}>
          <Spinner small />
        </Box>
      );
    }
    if (isError) {
      return (
        <div className="has-error" data-test="unit-history-error">
          <span className="help-block">
            {intl.formatMessage(messages.loadError)}{' '}
            <button
              type="button"
              className="btn btn-link"
              style={LINK_BUTTON_STYLE}
              onClick={refetch}
            >
              {intl.formatMessage(messages.tryAgain)}
            </button>
          </span>
        </div>
      );
    }
    return (
      // The dialog content already pads its bottom: no extra Bootstrap table margin.
      <table
        className="table table-striped"
        style={{ marginBottom: 0 }}
        data-test="unit-history-table"
      >
        <thead>
          <tr>
            <th>{intl.formatMessage(messages.unit)}</th>
            <th>{intl.formatMessage(messages.moveIn)}</th>
            <th>{intl.formatMessage(messages.moveOut)}</th>
            <th>{intl.formatMessage(messages.started)}</th>
            <th>{intl.formatMessage(messages.ended)}</th>
          </tr>
        </thead>
        <tbody>
          {stays.map((stay) => (
            <tr key={stay.stayLeaseId} data-test="unit-history-row">
              <td>{stay.unitLabel}</td>
              <td>
                {toDisplay(stay.moveInDate)} {pencil(stay)}
              </td>
              <td>{toDisplay(stay.moveOutDate)}</td>
              <td>{getStartedLabel(stay)}</td>
              <td>{getEndedLabel(stay)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={intl.formatMessage(messages.title)}
      actionsProps={[
        {
          children: intl.formatMessage(messages.close),
          onClick: onClose,
          variant: 'Cancel',
        },
      ]}
      ModalProps={{
        PaperProps: {
          'data-test': 'unit-history-modal',
          sx: {
            width: { xs: 'calc(100% - 32px)', md: 720 },
            maxWidth: 'calc(100% - 32px)',
          },
        },
      }}
    >
      <Typography
        variant="bodySmall"
        color="text.secondary"
        component="div"
        mb={1}
      >
        {intl.formatMessage(messages.subtitle)}
      </Typography>
      {renderBody()}
    </Modal>
  );
};

export default UnitHistoryModal;
